import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useIntl } from 'react-intl'
import queryString from 'query-string'
import _ from 'lodash'

import { getGlobalParams } from 'config/config'
import { updateUrlWithParams } from 'utils/url'
import { Button } from 'gdpp-components/dist/cjs/components/atoms'
import { dispatchFundCompareEvent, dispatchFundListSearchBox, dispatchFundListFundType } from 'utils/tracking'

import Grid from '../../atoms/FoundationGrid/Grid'
import Cell from '../../atoms/FoundationGrid/Cell'
import FilterGenerator from '../../molecules/FilterGenerator'
import FilterSelected from '../../molecules/FilterSelected'
import { favFundItemsSubject, fundsCountInfoSubject } from '../../../data/subjects/fundInfoItems'
import { getFilterStore } from '../../../data/subjects/filterStoreProducer'

import './style.scss'

const FundsFilter = ({ filterData, updateFilterData, downloadOptions }) => {
  const { baseUrl, favListName, fundComparisonCart, funds2CartJob, hasFundComparison, cart2FundJob } = getGlobalParams()
  const { funds2CartSubject } = funds2CartJob
  const { cart2FundSubject } = cart2FundJob
  const paramObj = queryString.parse(window.location.search)
  const { moreFilter } = paramObj
  const [isMoreFilter, setIsMoreFilter] = useState(moreFilter && moreFilter !== 'false')
  const [allFiltersData, setAllFiltersData] = useState()
  const [showMoreButton, setMoreButtonStatus] = useState()
  const [restFilterData, setRestFilterData] = useState()
  const [comparisonUrl, setComparisonUrl] = useState()

  const favItems = localStorage.getItem(favListName) ? JSON.parse(localStorage.getItem(favListName)) : []
  const comparisonItems = sessionStorage.getItem(fundComparisonCart) ? JSON.parse(sessionStorage.getItem(fundComparisonCart)) : []

  const allFiltersDataRef = useRef(allFiltersData)

  const setAllFiltersDataRef = (x) => {
    allFiltersDataRef.current = x
    setAllFiltersData(x)
  }

  const moreFilterFunc = () => {
    setIsMoreFilter(!isMoreFilter)
    updateUrlWithParams({ query: { moreFilter: !isMoreFilter } })
  }

  const intl = useIntl()

  const setFiltersInfo = (currentFilterData) => {
    const { blocks } = currentFilterData
    const isMoreButton = blocks.length > 1
    const restFilteredData = blocks.filter((val, index) => {
      return index !== 0
    })

    setMoreButtonStatus(isMoreButton)
    setAllFiltersDataRef(currentFilterData)
    setRestFilterData(restFilteredData)
  }

  const getCompareUrl = (currentCartItems) => {
    const compareFundsStr = `${baseUrl}/funds/compare${
      !_.isEmpty(currentCartItems) ? `?scIds=${currentCartItems.map((item) => item.shareClassId).join(',')}` : ''
    }`

    return compareFundsStr
  }

  const [favListCount, setFavListCount] = useState(favItems.length)

  const subscribeFavFundItemsCount = () => {
    const fundItemObservable = favFundItemsSubject.subscribe({
      next: (favFundItemsCount) => {
        setFavListCount(favFundItemsCount)
      }
    })

    return fundItemObservable
  }

  const [fundsCountInfo, setFundsCountInfo] = useState()

  const subscribeFundsCountInfo = () => {
    const fundsCountInfoObservable = fundsCountInfoSubject.subscribe({
      next: (countInfo) => {
        setFundsCountInfo(countInfo)
      }
    })

    return fundsCountInfoObservable
  }

  const [fundsComparisonInfo, setFundsComparisonInfo] = useState(comparisonItems)

  const subscribeFundsComparisonInfo = (subject) => {
    const fundsComparisonInfoObservable = subject.subscribe({
      next: (items) => {
        setComparisonUrl(getCompareUrl(items))
        setFundsComparisonInfo(items)
      }
    })

    return fundsComparisonInfoObservable
  }

  const handleFilterChange = (info) => {
    const filterStore = getFilterStore('FUND_LIST')
    const newState = filterStore.transferState(info)
    setFiltersInfo(newState.filterData)

    if (info) {
      if (info.type === 'input' && info.value?.trim() && info.id === 'shareClassName.zh||shareClassName.en||isin||taCode') {
        dispatchFundListSearchBox()
      }
      if (info.type !== 'input') {
        let action = ''
        const content = []
        newState.filterData?.blocks?.forEach((block) => {
          block?.elements?.forEach((element) => {
            if (element?.type === info.type && element?.key === info.id) {
              element.options?.forEach((option) => {
                if (option?.checked) {
                  content.push(option?.label)
                }
              })
              action = element.label
            }
          })
        })
        if (content.length > 0) {
          dispatchFundListFundType(action, content.join(', '))
        }
      }
    }

    setTimeout(() => {
      updateFilterData(newState.filterData)
    }, 0)
  }

  useEffect(() => {
    setFiltersInfo(filterData)
    const favFundItemsObservable = subscribeFavFundItemsCount()
    const fundsCountInfoObservable = subscribeFundsCountInfo()
    let fundsComparisonInfoOnservable = null
    let fundsComparisonInfoOnservableFromCart = null

    if (hasFundComparison) {
      fundsComparisonInfoOnservable = subscribeFundsComparisonInfo(funds2CartSubject)
      fundsComparisonInfoOnservableFromCart = subscribeFundsComparisonInfo(cart2FundSubject)
    }

    return () => {
      favFundItemsObservable.unsubscribe()
      fundsCountInfoObservable.unsubscribe()
      fundsComparisonInfoOnservable?.unsubscribe()
      fundsComparisonInfoOnservableFromCart?.unsubscribe()
    }
  }, [])

  return (
    <>
      {allFiltersDataRef.current ? (
        <>
          <Grid extraClasses="fund-filter__header">
            <Cell extraClasses="small-6 align-left text-left">
              <label id="fil-fund-list__title" className="fund-filter__title">
                {intl.formatMessage({ id: 'fundList.filter.fundFilter' })}
              </label>
              {fundsCountInfo && (
                <span className="fund-filter__funds-result-count hide-for-large">{`(${
                  fundsCountInfo.fundsNumerator !== fundsCountInfo.totalFundsCount ? `${fundsCountInfo.fundsNumerator}/` : ''
                }${fundsCountInfo.totalFundsCount})`}</span>
              )}
            </Cell>
            <Cell id="fil-fund-list__add-to-fav-btn-small" extraClasses="fund-filter__list text-right show-for-large">
              <Button
                kind="tertiary"
                icon="rating-star-ribbon"
                iconSize="16"
                iconPosition="left"
                target="_self"
                url={`${baseUrl}/funds/watchlist`}
                label={`${intl.formatMessage({ id: 'fundList.filter.myList' })}(${favListCount})`}
              />
              <Button
                kind="tertiary"
                icon="data-file-bars-search"
                iconSize="16"
                iconPosition="left"
                target="_self"
                url={comparisonUrl}
                label={`${intl.formatMessage({ id: 'fundList.filter.myCompare' })}(${fundsComparisonInfo.length})`}
                onClick={() => {
                  dispatchFundCompareEvent(
                    'compare - my fund comparison',
                    comparisonUrl.indexOf('scIds=') !== -1 ? comparisonUrl.substring(comparisonUrl.indexOf('scIds=') + 6) : ''
                  )
                }}
              />
            </Cell>
            <Cell id="fil-fund-list__download-funds-large" extraClasses="small-6 text-right align-self-middle hide-for-large">
              <Button
                kind="textlink"
                icon="download-bottom"
                iconPosition="left"
                iconSize="16"
                disabled={!downloadOptions.url || (fundsCountInfo && !fundsCountInfo.fundsNumerator)}
                label={downloadOptions.exportLinkContent}
                url={downloadOptions.url}
                target="_self"
                extraClasses="fil-fund-table__header__total-result__download small-5"
              />
            </Cell>
            <Cell id="fil-fund-list__add-to-fav-btn-large" extraClasses="small-12 fil-fund-list__add-to-fav-btn-large hide-for-large">
              <Button
                kind="tertiary"
                icon="rating-star-ribbon"
                size="small"
                iconSize="16"
                iconPosition="left"
                target="_self"
                url={`${baseUrl}/funds/watchlist`}
                label={`${intl.formatMessage({ id: 'fundList.filter.myList' })}(${favListCount})`}
              />
              <Button
                kind="tertiary"
                icon="data-file-bars-search"
                size="small"
                iconSize="16"
                iconPosition="left"
                target="_self"
                url={comparisonUrl}
                label={`${intl.formatMessage({ id: 'fundList.filter.myCompare' })}(${fundsComparisonInfo.length})`}
                onClick={() => {
                  dispatchFundCompareEvent(
                    'compare - my fund comparison',
                    comparisonUrl.indexOf('scIds=') !== -1 ? comparisonUrl.substring(comparisonUrl.indexOf('scIds=') + 6) : ''
                  )
                }}
              />
            </Cell>
          </Grid>
          <div className={classnames('fund-filter', allFiltersDataRef.current.extraClasses)}>
            <FilterGenerator
              filterData={allFiltersDataRef.current.blocks[0]}
              common={allFiltersDataRef.current.common}
              isMoreButton={showMoreButton}
              isMoreFilter={isMoreFilter}
              moreFilterFunc={moreFilterFunc}
              updateFilterData={handleFilterChange}
            />
            <div
              className={classnames({
                'fund-filter--hidden': !isMoreFilter,
                'fund-filter--show': isMoreFilter
              })}
            >
              {restFilterData.map((restFilterDataItem, index) => (
                <FilterGenerator
                  key={index}
                  filterData={restFilterDataItem}
                  common={allFiltersDataRef.current.common}
                  updateFilterData={handleFilterChange}
                />
              ))}
            </div>
          </div>
          <FilterSelected blocks={allFiltersDataRef.current.blocks} updateFilterData={handleFilterChange} />
        </>
      ) : null}
    </>
  )
}

FundsFilter.propTypes = {
  filterData: PropTypes.object.isRequired,
  updateFilterData: PropTypes.func,
  downloadOptions: PropTypes.object
}

FundsFilter.defaultProps = {
  updateFilterData: () => {},
  downloadOptions: {}
}

export default FundsFilter
